/* Compra rápida: el cajón que se abre desde la cesta de una tarjeta cuando el
   producto tiene formato o suscripción (snippets quick-buy-trigger,
   quick-buy-dialog y quick-buy-content; sección quick-buy).

   Un solo <dialog> por página. El contenido se pide al pulsar, con la Section
   Rendering API sobre la URL del propio producto, y se guarda: abrir dos veces
   la misma tarjeta no repite la petición.

   Sin este script no se rompe nada: el disparador es un enlace a la ficha. Y
   si la petición falla (sin red, o el escaparate pide contraseña), se va a la
   ficha igual: nunca se deja a nadie delante de un cajón vacío.

   Delegado en el documento, a propósito: las tarjetas llegan también después
   de cargar (filtros de la Tienda, «cargar más», el buscador).

   AÑADIR SIN SALIR DE LA PÁGINA. Los dos formularios de la compra rápida —el
   del botón de añadir directo de una tarjeta (`.quick-buy-form`) y el del
   cajón (`[data-quick-buy-form]`)— se envían por la Ajax Cart API
   (`/cart/add.js`) con su propio FormData: variante, cantidad y, si viaja,
   `selling_plan`. Al ir bien se cierra el cajón, se repinta el contador de la
   cabecera y sale un toast (evento `lny:toast`, assets/lny-toast.js); al ir
   mal, un toast de error con lo que diga Shopify y el botón vuelve a servir.
   Si la página lleva la cesta lateral (sections/cart-drawer), en vez del toast
   se abre ella, avisada por los eventos `lny:cart-updated` y `lny:cart-added`.
   Sin este script el formulario se envía como siempre y lleva a la cesta.
   ⚠️ Solo esos dos: la ficha y la cesta añaden por su cuenta. */
(() => {
  /* Lo mismo que hace la ficha con su formulario, y con sus mismos atributos
     (data-pdp-*): destapar el importe del formato y del tipo de compra
     elegidos. Los importes vienen pintados por Liquid; aquí no hay aritmética
     de dinero. */
  const bind = (root) => {
    const form = root.querySelector('[data-quick-buy-form]');
    if (!form || form.dataset.quickBuyBound) return;
    form.dataset.quickBuyBound = 'true';

    const prices = Array.from(root.querySelectorAll('[data-pdp-price]'));
    const planFields = root.querySelector('[data-pdp-plan-fields]');
    const planSelect = root.querySelector('[data-pdp-plan-select]');
    const addButton = root.querySelector('[data-pdp-add]');
    const quantity = form.querySelector('[data-quantity-input]');

    const sync = () => {
      const checkedVariant = form.querySelector('input[name="id"]:checked');
      const anyVariant = form.querySelector('[name="id"]');
      const variantId = checkedVariant ? checkedVariant.value : anyVariant ? anyVariant.value : '';
      const purchase = form.querySelector('input[name="purchase_type"]:checked');
      const purchaseType = purchase ? purchase.value : 'once';
      const subscribing = purchaseType === 'subscription';

      prices.forEach((el) => {
        const sameVariant = el.dataset.variant === variantId;
        const samePurchase = !el.dataset.purchase || el.dataset.purchase === purchaseType;
        el.hidden = !(sameVariant && samePurchase);
      });

      if (planFields) planFields.hidden = !subscribing;
      // Un select desactivado no viaja en el formulario: la compra única nunca
      // arrastra un selling_plan por error.
      if (planSelect && planSelect.name) planSelect.disabled = !subscribing;

      /* El tope de la cantidad es el stock del formato elegido, cuando se
         conoce (`data-stock-max` en su radio). Se pone en el `max` del campo y
         se avisa al stepper (assets/lny-quantity-stepper.js), que recorta la
         cantidad si se pasa y apaga «+». Con un solo formato no hay radios:
         el `max` que pintó Liquid no cambia. */
      if (quantity && checkedVariant) {
        const stock = Number(checkedVariant.dataset.stockMax);
        const known = checkedVariant.dataset.stockMax !== undefined && stock >= Number(quantity.min || 1);
        if (known) quantity.max = String(stock);
        else quantity.removeAttribute('max');
        quantity.dispatchEvent(new CustomEvent('lny:quantity-refresh', { bubbles: true }));
      }

      if (addButton) {
        const available = checkedVariant ? checkedVariant.disabled === false : true;
        addButton.disabled = !available;
        const label = available ? addButton.dataset.labelAdd : addButton.dataset.labelSoldOut;
        if (label) addButton.textContent = label;
      }
    };

    form.addEventListener('change', sync);
    sync();
  };

  // El contenido que ya venga pintado en la página (la guía de estilo)
  document.querySelectorAll('[data-quick-buy-content]').forEach(bind);

  const dialog = document.querySelector('[data-quick-buy-dialog]');
  if (!dialog) return;

  /* ── Añadir a la cesta sin salir de la página ─────────────────────────── */
  const ADD_FORMS = 'form.quick-buy-form, form[data-quick-buy-form]';
  const texts = dialog.dataset;

  const toast = (detail) => document.dispatchEvent(new CustomEvent('lny:toast', { detail }));

  /* El contador de la cabecera: se sustituye por el que pinta Liquid (sección
     `cart-count`), que llega con la respuesta de añadir. Si no viniera, se
     pide aparte; y si eso también falla, el contador se queda como estaba:
     el producto está añadido igual y el toast ya lo ha dicho. */
  const paintCount = (html) => {
    if (!html) return false;
    const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-count]');
    if (!fresh) return false;
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.innerHTML = fresh.innerHTML;
    });
    return true;
  };

  const refreshCount = (sections) => {
    if (paintCount(sections && sections['cart-count'])) return;
    const url = new URL(texts.rootUrl || '/', window.location.href);
    url.searchParams.set('sections', 'cart-count');
    fetch(url.toString(), { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .then((json) => paintCount(json && json['cart-count']))
      .catch(() => {});
  };

  const setBusy = (form, button, busy) => {
    if (busy) form.dataset.quickBuyBusy = 'true';
    else delete form.dataset.quickBuyBusy;
    if (!button) return;
    button.disabled = busy;
    if (busy) button.setAttribute('aria-busy', 'true');
    else button.removeAttribute('aria-busy');
  };

  const add = (form, button) => {
    const hadFocus = button && document.activeElement === button;
    // El FormData se lee ANTES de apagar nada: un control apagado no viaja
    const data = new FormData(form);
    // Con la cesta lateral en la página (sections/cart-drawer) se pide también
    // su HTML: se repinta y se abre con lo recién añadido
    const drawer = document.querySelector('[data-cart-drawer]');
    data.append('sections', drawer ? 'cart-count,cart-drawer' : 'cart-count');
    data.append('sections_url', window.location.pathname);
    setBusy(form, button, true);

    fetch(texts.cartAddUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: data,
    })
      .then((response) =>
        response
          .json()
          .catch(() => null)
          .then((json) => ({ ok: response.ok, json }))
      )
      .then(({ ok, json }) => {
        // Sin JSON: no era la API (el escaparate con contraseña responde HTML)
        if (!ok || !json || json.status) {
          const reason = json && typeof json.description === 'string' ? json.description : '';
          throw new Error(reason);
        }
        if (dialog.open && dialog.contains(form)) dialog.close();
        refreshCount(json.sections);
        if (drawer) {
          document.dispatchEvent(new CustomEvent('lny:cart-updated', { detail: json }));
          document.dispatchEvent(new CustomEvent('lny:cart-added', { detail: json }));
          // La cesta lateral abierta ya es la confirmación: sin toast encima
          if (drawer.dataset.openOnAdd === 'true') return;
        }
        toast({
          type: 'success',
          message: texts.labelAdded,
          detail: json.title || json.product_title || '',
          action: { label: texts.labelViewCart, url: texts.cartUrl },
        });
      })
      .catch((error) => {
        // Un fallo de red trae el mensaje del navegador, en inglés: solo se
        // enseña lo que haya dicho Shopify
        const fromShop = error instanceof TypeError ? '' : error.message;
        toast({ type: 'error', message: fromShop || texts.labelAddError });
      })
      .finally(() => {
        setBusy(form, button, false);
        // En el cajón, el botón vuelve a lo que diga el formato elegido
        // (agotado o no): eso lo sabe `sync`, que escucha `change`
        form.dispatchEvent(new Event('change'));
        // Apagar un botón con el foco se lo quita: se le devuelve
        if (hadFocus && button.isConnected && !button.disabled) button.focus();
      });
  };

  if (texts.cartAddUrl && typeof window.fetch === 'function' && typeof window.FormData === 'function') {
    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (!form.matches || !form.matches(ADD_FORMS) || event.defaultPrevented) return;
      event.preventDefault();
      // Una petición por formulario: el segundo clic no envía otra
      if (form.dataset.quickBuyBusy) return;
      add(form, event.submitter || form.querySelector('[type="submit"]'));
    });
  }

  if (typeof dialog.showModal !== 'function') return;

  const body = dialog.querySelector('[data-quick-buy-body]');
  const status = dialog.querySelector('[data-quick-buy-status]');
  const cache = new Map();
  let opener = null;
  let turn = 0;

  const say = (state) => {
    status.textContent = state ? status.dataset[state] : '';
    status.hidden = !state;
  };

  const load = (href) => {
    if (cache.has(href)) return Promise.resolve(cache.get(href));
    const url = new URL(href, window.location.href);
    url.searchParams.set('section_id', 'quick-buy');
    url.hash = '';
    return fetch(url.toString(), { credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) throw new Error(String(response.status));
        return response.text();
      })
      .then((html) => {
        const content = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-quick-buy-content]');
        // Sin contenido: un descatalogado, o una página que no es la sección
        // (el escaparate con contraseña redirige y responde 200).
        if (!content) throw new Error('empty');
        cache.set(href, content.outerHTML);
        return content.outerHTML;
      });
  };

  const open = (trigger) => {
    const href = trigger.href;
    const mine = ++turn;
    opener = trigger;

    body.replaceChildren();
    say('loading');
    dialog.setAttribute('aria-labelledby', status.id);
    dialog.setAttribute('aria-busy', 'true');
    if (!dialog.open) dialog.showModal();

    load(href)
      .then((html) => {
        // Se cerró, o se pulsó otra tarjeta, mientras llegaba
        if (mine !== turn || !dialog.open) return;
        body.innerHTML = html;
        const content = body.querySelector('[data-quick-buy-content]');
        say('');
        dialog.removeAttribute('aria-busy');
        if (content.dataset.titleId) dialog.setAttribute('aria-labelledby', content.dataset.titleId);
        bind(content);
      })
      .catch(() => {
        if (mine !== turn) return;
        say('error');
        window.location.assign(href);
      });
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest ? event.target.closest('[data-quick-buy]') : null;
    // `defaultPrevented`: el carrusel de la home ya se ha quedado el clic (un
    // arrastre, o una tarjeta lateral que pasa al centro).
    if (!trigger || event.defaultPrevented) return;
    // Abrir en otra pestaña sigue siendo cosa del navegador
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    open(trigger);
  });

  dialog.querySelector('[data-quick-buy-close]').addEventListener('click', () => dialog.close());
  // Clic en el velo. El <dialog> es el objetivo fuera de la caja… y también en
  // su propio relleno, así que se mira dónde cayó el clic: tocar el margen
  // interior del cajón no lo cierra.
  dialog.addEventListener('click', (event) => {
    if (event.target !== dialog) return;
    const box = dialog.getBoundingClientRect();
    const inside =
      event.clientX >= box.left && event.clientX <= box.right && event.clientY >= box.top && event.clientY <= box.bottom;
    if (!inside) dialog.close();
  });
  dialog.addEventListener('close', () => {
    turn += 1;
    if (opener && opener.isConnected) opener.focus();
    opener = null;
  });

  // Al volver atrás desde la cesta el navegador puede devolver la página tal
  // como quedó, con el cajón abierto: se cierra.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted && dialog.open) dialog.close();
  });
})();
